import { useQueryClient } from '@tanstack/react-query';

import { RequestBusinessPartnerEndCustomerTypeEnum } from '../../api/generated/pm';
import type {
  GetV1BusinessPartnerCurrentMigrationCandidateUsersResponse,
  GetV1IdentityCurrentMigrationCandidateBusinessPartnersResponse,
} from '../../api/generated/pm';
import {
  getV1BusinessPartnerCurrentMigrationCandidateUsersQueryKey,
  getV1IdentityCurrentMigrationCandidateBusinessPartnersQueryKey,
} from '../../api/generated/pm/@tanstack/react-query.gen';
import { useAppService } from '../../providers/app/app.context';
import featureFlags from '../../utils/featureFlags';
import {
  migrationCandidateBusinessPartnersMockData,
  migrationCandidateUsersMockData,
} from '../../utils/mockData';

export enum AppRoutes {
  Overview = '/',
  Companies = '/companies',
  CompaniesList = '/companies/list',
  JoinRequests = '/companies/join-requests',
  Invites = '/companies/invites',
  UnclaimedOrganizations = '/companies/review-organizations',
  Divisions = '/divisions',
  Users = '/users',
  Requests = '/users/requests',
  Support = '/support',
  Organizations = '/support/organizations',
  TopconId = '/support/topconid',
  Apps = '/apps',
  Settings = '/settings',
  SettingsGeneralInformation = '/settings/general-information',
  SettingsAccess = '/settings/access',
  SettingsDeactivateAccount = '/settings/deactivate-account',
  SettingsDomainRegistration = '/settings/domain-verification',
  SettingsIdentityProviders = '/settings/identity-providers',
  SettingsLocale = '/settings/locale',
  SettingsNotifications = '/settings/notifications',
  SettingsProjects = '/settings/projects',
  SettingsCustomApps = '/settings/custom-apps',
  SettingsLegalDocuments = '/settings/legal-documents',
  Reports = '/reports',
  NoBP = '/nobp',
  AboutMe = '/about-me',
  NotFound = '/404',
  Unauthorized = '/401',
  BadRequest = '/400',
  Wildcard = '/*',
  Playground = '/playground',
  DevSettings = '/dev-settings',
  Notifications = '/notifications',
  ServiceAgreement = '/service-agreement/:id',
  Error = '/error/:code',
  SignIns = '/sign-ins',
  Password = '/password',
  Dealers = '/dealers',
  Customers = '/customers',
  CustomersList = '/customers/list',
  CustomerInvites = '/customers/invites',
  CustomerRequests = '/customers/requests',
  CustomerSuggestions = '/customers/suggestions',
  Products = '/products',
  ReviewRequestBussinessPartner = '/share/new-customer',
  ReviewRequestChangeOwnership = '/share/accept-change-ownership',
  LicensesOverview = '/licenses-overview',
}

/** Routes to page using given :id */
export const idRouter = (route: AppRoutes, id: string) => route.replace(':id', id);

export function useIsNotCompany() {
  const { currentBusinessPartner } = useAppService();

  return currentBusinessPartner?.type !== 'end_customer_enterprise';
}

export function useIsDealer() {
  const { currentBusinessPartner, isLoading } = useAppService();
  return !isLoading && currentBusinessPartner?.type === BusinessPartnerType.Dealer;
}

export function useIsLogged() {
  const { identity, isLoading } = useAppService();
  return !!identity && !isLoading;
}

export function useIsDivision() {
  const { currentBusinessPartner, loadedBusinessPartner } = useAppService();

  return loadedBusinessPartner && !!currentBusinessPartner?.parentBusinessPartnerId;
}

// CUD = Create, Update, Delete
// 1 - Only top-level end-customer enterprises can CUD divisions,
// 2 - Only users with any role can CUD divisions
export function useCanCUDDivisions() {
  const { currentBusinessPartner, loadedBusinessPartner, userRoles } = useAppService();
  return (
    loadedBusinessPartner &&
    currentBusinessPartner &&
    !currentBusinessPartner.parentBusinessPartnerId &&
    currentBusinessPartner.type ===
      RequestBusinessPartnerEndCustomerTypeEnum.END_CUSTOMER_ENTERPRISE &&
    userRoles &&
    userRoles.length > 0
  );
}

// TODO: Needs to be updated when TOPCON-UTILS is updated
export enum BusinessPartnerType {
  Topcon = 'topcon',
  Dealer = 'dealer',
  EndCustomerEnterprise = 'end_customer_enterprise',
  EndCustomerIndividual = 'end_customer_individual',
}

export function useHideUnclaimedOrganizations() {
  // Read from the query cache without subscribing via useQuery, which would add
  // extra hooks to the Sidebar component. The Sidebar conditionally calls children's
  // `hidden` hooks depending on the parent's visibility, so using useQuery here
  // would violate the Rules of Hooks when the parent visibility toggled.
  const queryClient = useQueryClient();

  if (!featureFlags.MIGRATION) {
    return true;
  }

  // Key-factory options must stay byte-identical to
  // useMigrationCandidateBusinessPartners in src/hooks/queries/migration.queries.ts.
  const data = featureFlags.MIGRATION_MOCK_DATA
    ? migrationCandidateBusinessPartnersMockData
    : queryClient.getQueryData<GetV1IdentityCurrentMigrationCandidateBusinessPartnersResponse>(
        getV1IdentityCurrentMigrationCandidateBusinessPartnersQueryKey()
      );
  return !data?.items || data.items.length === 0;
}

export function useHideCustomerSuggestions() {
  const queryClient = useQueryClient();

  if (!featureFlags.MIGRATION) {
    return true;
  }

  // Key-factory options must stay byte-identical to useMigrationCandidateUsers
  // in src/hooks/queries/migration.queries.ts.
  const data = featureFlags.MIGRATION_MOCK_DATA
    ? migrationCandidateUsersMockData
    : queryClient.getQueryData<GetV1BusinessPartnerCurrentMigrationCandidateUsersResponse>(
        getV1BusinessPartnerCurrentMigrationCandidateUsersQueryKey()
      );
  return !data?.items || data.items.length === 0;
}
